import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  Pipe,
  PipeTransform,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { FromDictionaryPipe, SetValueToDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
import { JsonLdComponent } from '../../../seo/components/json-ld/json-ld.component';
import { JudgeMeReview } from '../../models/judge-me-json-response.model';

@Pipe({
  name: 'safeHtml',
})
export class SafeHtmlPipe implements PipeTransform {
  #sanitizer = inject(DomSanitizer);

  transform(value: string) {
    return this.#sanitizer.bypassSecurityTrustHtml(value);
  }
}

// Detects whether its host element is currently being visually truncated (e.g. by
// -webkit-line-clamp), so a "read more" button can be shown only when text is actually cut off.
@Directive({
  selector: '[appOverflowDetector]',
  exportAs: 'overflowDetector',
})
export class OverflowDetectorDirective {
  #el = inject(ElementRef<HTMLElement>).nativeElement;
  readonly isOverflowing = signal(false);

  constructor() {
    afterNextRender(() => this.check());
  }

  check() {
    this.isOverflowing.set(this.#el.scrollHeight > this.#el.clientHeight + 1);
  }
}

@Component({
  selector: 'app-product-reviews',
  imports: [SafeHtmlPipe, DatePipe, OverflowDetectorDirective, FromDictionaryPipe, SetValueToDictionaryPipe, JsonLdComponent],
  templateUrl: './product-reviews.component.html',
  styleUrl: './product-reviews.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductReviewsComponent {
  readonly reviews = input.required<JudgeMeReview[]>();

  protected readonly stars = [1, 2, 3, 4, 5];
  #expandedReviews = signal<ReadonlySet<string>>(new Set());

  protected readonly averageRating = computed(() => {
    const reviews = this.reviews();
    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    return totalRating / reviews.length;
  });
  protected readonly averageRatingRounded = computed(() => Math.round(this.averageRating()));
  protected readonly averageRatingFixed = computed(() => this.averageRating().toFixed(1));
  protected readonly reviewCount = computed(() => this.reviews().length);

  protected readonly reviewSchema = computed(() => {
    const reviews = this.reviews();
    if (!reviews.length) {
      return null;
    }

    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      'name': reviews[0].product_title,
      'url': reviews[0].product_url,
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': this.averageRatingFixed(),
        'reviewCount': this.reviewCount(),
      },
      'review': reviews.map(review => ({
        '@type': 'Review',
        'datePublished': review.created_at,
        'name': review.title || undefined,
        'reviewBody': review.body,
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': review.rating,
          'bestRating': 5,
          'worstRating': 1,
        },
        'author': {
          '@type': 'Person',
          'name': review.is_anonymous_reviewer ? 'Anonymous' : review.reviewer_name,
        },
      })),
    };
  });

  protected isExpanded(uuid: string) {
    return this.#expandedReviews().has(uuid);
  }

  protected toggleExpanded(uuid: string) {
    this.#expandedReviews.update(current => {
      const next = new Set(current);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  }
}
